import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TWButton, TWInput, SegmentedToggle } from '../../../components/ui';
import MediaField from './MediaField';
import { isMediaBlock } from '../release-note-editor-model';

const labels = { heading: 'Heading', paragraph: 'Paragraph', list: 'List', image: 'Image', video: 'Video' };

const areaCls =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400';

// One block in the Content list (spec §7.3): a type rail with move buttons,
// the block's own editor, a remove button, and the validation message under
// it. On a phone the rail becomes one row above the editor (designer brief).
// `locked` (an upload is in flight) freezes the order: no move, no remove.
const BlockCard = ({ block, index, count, error, canUpload, saved = true, uploading, locked = false, onChange, onMove, onRemove, onPick }) => {
  const items = block.items || [''];
  const setItem = (i, value) => onChange({ items: items.map((it, j) => (j === i ? value : it)) });
  return (
    <div
      className={`grid grid-cols-[minmax(0,1fr)_2.5rem] gap-3 rounded-lg border bg-white p-3.5 dark:bg-slate-800 sm:grid-cols-[7rem_minmax(0,1fr)_2.5rem] ${
        error ? 'border-rose-300 dark:border-rose-500/60' : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      <div className="flex flex-row flex-wrap items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 sm:flex-col sm:items-start">
        <span>{labels[block.type]}</span>
        {block.type === 'heading' ? (
          <SegmentedToggle
            size="sm"
            ariaLabel="Heading level"
            value={String(block.level)}
            onChange={(v) => onChange({ level: Number(v) })}
            options={[{ value: '2', label: 'H2' }, { value: '3', label: 'H3' }]}
          />
        ) : null}
        <span className="flex gap-1">
          <TWButton variant="ghost" size="sm" icon={ArrowUpIcon} aria-label="Move up" disabled={locked || index === 0} onClick={() => onMove(-1)} />
          <TWButton variant="ghost" size="sm" icon={ArrowDownIcon} aria-label="Move down" disabled={locked || index === count - 1} onClick={() => onMove(+1)} />
        </span>
      </div>

      <div className="order-last col-span-2 flex min-w-0 flex-col gap-2 sm:order-none sm:col-span-1">
        {block.type === 'heading' ? (
          <TWInput size="sm" aria-label="Heading text" value={block.text} onChange={(e) => onChange({ text: e.target.value })} placeholder="Heading" />
        ) : null}
        {block.type === 'paragraph' ? (
          <textarea aria-label="Paragraph text" rows={4} className={areaCls} value={block.text} onChange={(e) => onChange({ text: e.target.value })} placeholder="Write a paragraph" />
        ) : null}
        {block.type === 'list' ? (
          <>
            {items.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <TWInput size="sm" aria-label={`List item ${i + 1}`} value={item} onChange={(e) => setItem(i, e.target.value)} placeholder="List item" />
                <TWButton variant="ghost" size="sm" icon={XMarkIcon} aria-label={`Remove item ${i + 1}`} disabled={items.length === 1} onClick={() => onChange({ items: items.filter((_, j) => j !== i) })} />
              </div>
            ))}
            <TWButton variant="link" size="sm" icon={PlusIcon} label="Add item" onClick={() => onChange({ items: [...items, ''] })} className="self-start" />
          </>
        ) : null}
        {isMediaBlock(block) ? <MediaField block={block} canUpload={canUpload} saved={saved} uploading={uploading} onPick={onPick} /> : null}
        {error ? <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p> : null}
      </div>

      <TWButton variant="ghost" size="sm" icon={TrashIcon} aria-label="Remove block" disabled={locked} onClick={onRemove} className="justify-self-end !text-slate-500 hover:!text-rose-600 dark:hover:!text-rose-400" />
    </div>
  );
};

export default BlockCard;
